<template>
  <div class="p-4 d-flex flex-column">
    <h2 :class="['h4', 'pb-2']" v-html="$t(currentStage.title)" />

    <div v-if="currentStage === stages.LIST_INVOICES_STAGE">
      <h2 class="h5">
        {{ $t('Amount:', {amount: $n(totalSelectedExcludingVAT,'currency')}) }}
      </h2>
      <h3 class="h5 pb-2 w-50 border-bottom">
        {{ $t('VAT:', {vat: $n(totalSelectedVAT,'currency')}) }}
      </h3>
      <h4>
        {{ $t('Total:', {totalAmt: $n(totalSelectedIncludingVAT,'currency')}) }}
      </h4>
      <b-form-invalid-feedback :state="totalSelectedIncludingVAT > 0.5">
        {{ $t('The Total must be greater than &pound;0.50') }}
      </b-form-invalid-feedback>
      <b-button :disabled="invoices.length === 0 || totalSelectedIncludingVAT < 0.5" size="sm" class="mt-3" variant="primary" @click="onListNext">
        {{ $t('Next: Choose payment method') }}
      </b-button>
    </div>

    <div v-else-if="isSelectPaymentStage">
      <b-form-group class="mt-2">
        <b-form-radio
          v-for="payment in paymentMethods"
          :key="payment.id"
          v-model="selectedPaymentMethod"
          :value="payment.authoritycode"
        >
          {{ $t('Card ending', {lastFourDigits: payment.cardnum.slice(-4), paymentDefault: payment.default ? $t('(Default)'): ''}) }}
        </b-form-radio>
        <hr>
        <b-form-radio v-model="selectedPaymentMethod" value="new">
          {{ $t('Pay with a new card') }}
        </b-form-radio>
      </b-form-group>
      <b-button v-if="selectedPaymentMethod === 'new'" class="mt-3" size="sm" variant="primary" @click="onCardSelectNext">
        {{ $t('Next') }}
      </b-button>
    </div>

    <div v-else-if="isNewPaymentStage">
      <new-payment-method ref="newPaymentMethod" :isPaymentMethodPage="false" :isCardSetup="false" @created="onNewCard" />
    </div>

    <div v-if="shouldDisplayInvoiceList" class="invoice-list-container mt-4">
      <b-table-simple
        :class="['bg-white', ' table-hover']"
        no-border-collapse
        small
      >
        <tbody>
          <tr v-for="invoice in invoices" :key="invoice.id" class="itemised-invoice">
            <td class="py-2 align-middle">
              {{ invoice.created_date | parseDate }} <br>
              {{ invoice.code }}
            </td>
            <td v-if="currentStage !== stages.SUCCESS_STAGE" class="py-2 d-flex itemised-invoice align-items-center justify-content-between align-content-center">
              {{ invoice.description }}
              <b-button variant="link" @click="$emit('invoice-removed', invoice)">
                <font-awesome-icon class="text-remove" icon="minus-circle" />
              </b-button>
            </td>
          </tr>
        </tbody>
      </b-table-simple>
    </div>

    <div v-if="isPaymentMethodStage" class="order-2">
      <validation-observer ref="observer">
        <validation-provider v-slot="{errors, invalid}" rules="min_value:0.50">
          <h2 class="h5">
            {{ $t('Amount:', {amount: $n(totalSelectedExcludingVAT,'currency')}) }}
          </h2>
          <h3 class="h5 pb-2 w-50 border-bottom">
            {{ $t('VAT:', {vat: $n(totalSelectedVAT,'currency')}) }}
          </h3>
          <h4>
            {{ $t('Total:', {totalAmt: $n(totalSelectedIncludingVAT,'currency')}) }}
          </h4>
          <input v-model="totalSelectedIncludingVAT" type="hidden">
          <b-form-invalid-feedback :state="invalid" :force-show="invalid">
            {{ errors[0] }}
          </b-form-invalid-feedback>
        </validation-provider>
        <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" :rules="{ required: { allowFalse: false } }">
          <b-form-group class="mt-4">
            <b-form-checkbox v-model="confirmedPayment" :state="(dirty || validated) ? (valid ? null : false) : null">
              {{ $t('I agree to paying the amount shown with the payment method selected') }}
            </b-form-checkbox>
            <b-form-invalid-feedback :state="invalid" :force-show="invalid">
              {{ errors[0] }}
            </b-form-invalid-feedback>
          </b-form-group>
        </validation-provider>
        <b-button v-if="!busy" size="sm" class="mt-3 mr-2" variant="outline-secondary" @click="onBackNavigation">
          {{ $t('Back') }}
        </b-button>
        <b-button v-if="!busy" size="sm" class="mt-3" variant="primary" @click="onPayNow">
          {{ $t('Pay Now') }}
        </b-button>
        <spinner v-else />
      </validation-observer>
    </div>
  </div>
</template>

<script>
const LIST_INVOICES_STAGE = {title: 'Your Selected Invoices are Itemised Below'};
const SELECT_PAYMENT_STAGE = {title: 'Choose a Payment Method'};
const NEW_PAYMENT_METHOD_STAGE = {title: 'Add Card Details'};
const CONFIRMATION_STAGE = {title: 'Confirm Payment'};
const SUCCESS_STAGE = {title: 'Thank you. <br /> Your Selected Invoices have been Paid.'};

import NewPaymentMethod from '@/components/form/NewPaymentMethod';
import Spinner from '@/components/animation/Spinner';
import {loadStripe} from '@stripe/stripe-js';
import userDevice from "@/imports/mixins/userDevice";
import {ValidationProvider, ValidationObserver} from 'vee-validate';
import notification from '@/imports/mixins/notification';
import stripeKey from "@/imports/mixins/stripeKey";
import company from "@/imports/mixins/company";

export default {
  name: "InvoicePayment",
  components: {NewPaymentMethod, Spinner, ValidationProvider, ValidationObserver},
  mixins: [userDevice, notification, company, stripeKey],
  props: ['invoices'],
  data() {
    return {
      stages: {
        LIST_INVOICES_STAGE,
        SELECT_PAYMENT_STAGE,
        NEW_PAYMENT_METHOD_STAGE,
        CONFIRMATION_STAGE,
        SUCCESS_STAGE,
      },
      currentStage: LIST_INVOICES_STAGE,
      paymentMethods: [],
      selectedPaymentMethod: null,
      stripe: null,
      secretKey: null,
      confirmedPayment: false,
      isPaymentMethodPage: false,
      busy: false,
    }
  },
  computed: {
    totalSelectedIncludingVAT: function() {
      return this.invoices.reduce((total, invoice) => total + invoice.price_including_vat, 0);
    },
    totalSelectedVAT: function() {
      return this.invoices.reduce((total, invoice) => total + invoice.price_vat, 0);
    },
    totalSelectedExcludingVAT: function() {
      return this.invoices.reduce((total, invoice) => total + invoice.price_excluding_vat, 0);
    },
    shouldDisplayInvoiceList: function () {
      return this.currentStage === (this.stages.LIST_INVOICES_STAGE);
    },
    isSelectPaymentStage: function() {
      return this.currentStage === this.stages.SELECT_PAYMENT_STAGE
    },
    isPaymentMethodStage: function() {
      return this.isNewPaymentStage || (this.isSelectPaymentStage && this.selectedPaymentMethod !== 'new')
    },
    isNewPaymentStage: function() {
      return this.currentStage === this.stages.NEW_PAYMENT_METHOD_STAGE
    }
  },
  methods: {
    loadPaymentMethods() {
      this.$api.payments.getPaymentMethods().then((result) => {
        this.paymentMethods = result.data.data;
        if (this.paymentMethods.length === 0) {
          this.selectedPaymentMethod = 'new';
        } else{
          for (const payment of result.data.data) {
            if (parseInt(payment.default) === 1 && !this.isNewPaymentStage) {
              this.selectedPaymentMethod = payment.authoritycode;
            }
          }
        }
      });
    },
    onCardSelectNext() {
      if(this.selectedPaymentMethod === 'new') {
        this.currentStage = NEW_PAYMENT_METHOD_STAGE;
      } else {
        this.currentStage = CONFIRMATION_STAGE;
      }
    },
    onNewCard(paymentMethod) {
      this.selectedPaymentMethod = paymentMethod;
      this.onPayNow();
    },
    onPayNow() {
      this.busy = true;
      if (this.selectedPaymentMethod === 'new') {
        this.$refs.newPaymentMethod.submit().finally(() => {
          this.busy = false;
        });
      } else {
        this.pay();
      }
    },
    onBackNavigation() {
      this.currentStage = LIST_INVOICES_STAGE;
    },
    pay() {
      this.$refs.observer.validate().then((success) => {
        if (success) {
          this.busy = true;
          const invoiceCodes = this.invoices.map((invoice) => invoice.code);
          this.$api.payments.createPaymentIntent(this.selectedPaymentMethod, invoiceCodes).then(({data}) => {
            this.stripe.handleCardPayment(data.data.secret, {
              payment_method: this.selectedPaymentMethod
            }).then((response) => {
              if (response.error) {
                this.notify(this.$bvToast, response.error.message, null, 'danger');
                this.currentStage = SELECT_PAYMENT_STAGE;
                this.selectedPaymentMethod = 'new';
                this.busy = false;
              } else if (response.paymentIntent.status === 'succeeded') {
                // Timeout to allow stripe to call the webhook and update the affected invoices
                window.setTimeout(() => {
                  this.busy = false;
                  this.currentStage = SUCCESS_STAGE;
                  this.$emit('complete');
                  this.$store.dispatch("accountBalance");
                }, 2000);
              }
            })
              .catch(() => this.busy = false);
          });
        } else {
          this.busy = false;
        }
      }).catch(() => this.busy = false);
    },
    onListNext() {
      if (this.paymentMethods.length === 0) {
        this.currentStage = NEW_PAYMENT_METHOD_STAGE;
        this.selectedPaymentMethod = 'new';
      } else {
        this.currentStage = SELECT_PAYMENT_STAGE;
      }
    },
    onCancel() {
      this.$emit('cancel-payment');
    }
  },
  created() {
    this.loadPaymentMethods();
    loadStripe(this.stripeKey()).then((stripe) => this.stripe = stripe);
  }
}
</script>

<style scoped lang="scss">
@media only screen and (max-width: 414px) {
  .table tbody {
    .itemised-invoice {
      display: flex;
      width: 100%;
      flex-wrap: wrap;
      .align-middle {
        flex: 0 1 100%;
      }
    }
  }
}

.invoice-list-container {
  overflow-y: auto;
  @media only screen and (min-height: 767px) and (max-height: 880px) {
    max-height: 295px;
  }

  @media only screen and (min-height: 881px) and (max-height: 1024px) {
    max-height: 375px;
  }

  @media only screen and (min-height: 1024px) {
    max-height: 535px;
  }
}
</style>
